import { captureError } from '@smg-real-estate/browser-rum';
import {
  getFeatureFlags,
  type SmgSplitSession,
  SplitSupportedEnvironment,
  SplitSupportedPlatform,
} from '@smg-real-estate/vue-experiments';

import { SplitFeatureFlags } from './splitFeatureFlags';

const isServer = (): boolean => import.meta.env.SSR;

const defaultSmgSplitSession: SmgSplitSession = {
  splitSessionKey: '',
  splitTreatmentsWithConfig: {},
};

const GLOBAL_FLAG_SET = 'global';

const getSplitSupportedEnv = (): SplitSupportedEnvironment => {
  if (isServer()) {
    return process.env.VUE_STAGE === 'prod'
      ? SplitSupportedEnvironment.PROD
      : SplitSupportedEnvironment.TEST;
  } else {
    return import.meta.env.VUE_STAGE === 'prod'
      ? SplitSupportedEnvironment.PROD
      : SplitSupportedEnvironment.TEST;
  }
};

export async function fetchSmgSplitSession(
  renderPath: string,
): Promise<SmgSplitSession | undefined> {
  try {
    if (Object.keys(SplitFeatureFlags).length === 0) {
      return defaultSmgSplitSession;
    }
    const supportedPlatform = isServer()
      ? (process.env.VUE_APP_PLATFORM as SplitSupportedPlatform)
      : (import.meta.env.VUE_APP_PLATFORM as SplitSupportedPlatform);

    const splitIOFlagSets = getSplitIOFlagSetsByRenderPath(renderPath, supportedPlatform);

    if (!splitIOFlagSets || splitIOFlagSets.length === 0) {
      return defaultSmgSplitSession;
    }

    const featureFlagsResponse = await getFeatureFlags(splitIOFlagSets, {
      platform: supportedPlatform,
      env: getSplitSupportedEnv(),
    });

    const { userKey, treatmentsWithConfig } = featureFlagsResponse.data.data;
    return {
      splitSessionKey: userKey,
      splitTreatmentsWithConfig: treatmentsWithConfig,
    };
  } catch (e) {
    captureError(e);
    return undefined;
  }
}

// NOTE: Duplicated in lambda/helpers.js; see comment there for rationale.
function getSplitIOFlagSetsByRenderPath(renderPath: string, platform: string) {
  const buyTerms = '(buy|acquistare|acheter|kaufen)';
  const rentTerms = '(rent|affittare|louer|mieten)';

  const urlMap: Record<string, any> = {
    hg: {
      // PDP Buy & Rent
      [`/${buyTerms}/[0-9]+`]: 'pdp_buy',
      [`/${rentTerms}/[0-9]+`]: 'pdp_rent',
    },
    is24: {
      // PDP Buy & Rent
      [`/${buyTerms}/[0-9]+`]: 'pdp_buy',
      [`/${rentTerms}/[0-9]+`]: 'pdp_rent',
    },
  };

  const platformMap = urlMap[platform];
  if (!platformMap) {
    return null;
  }

  for (const key in platformMap) {
    const regex = new RegExp(key);
    if (regex.test(renderPath)) {
      return [platformMap[key], GLOBAL_FLAG_SET];
    }
  }

  return [GLOBAL_FLAG_SET];
}
